import { useCallback } from 'react';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useAuthStore } from '@/stores/authStore';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { configuredExternalUrl } from '@/config/serverConfig';
import { handleSearchError, type SearchErrorContext } from '@/lib/utils/searchErrorHandler';
import { formatPersistentSnackbarError } from '@/lib/utils/gridSearchUi';
import { useSearchUiStore } from '@/stores/searchUiStore';
import type { SnackbarSeverity } from '@/stores/snackbarStore';
import type { SupportInfo } from '@/lib/utils/gridSearchUi';

/**
 * Shows a persistent snackbar for failed table search POSTs and signals search panels
 * to reopen their filter section.
 */
export function useGridSearchErrorHandler(contextLabel: string) {
  const { showSnackbar } = useSnackbar();
  const currentUser = useAuthStore((state) => state.currentUser);
  const systemConfig = useSystemConfig();
  const bugReportUrl = configuredExternalUrl(systemConfig?.bugReport);
  const dataManagerEmail = systemConfig?.dataManagerEmail?.trim() || undefined;
  const reportSearchFailure = useSearchUiStore((state) => state.reportSearchFailure);

  const handleGridSearchError = useCallback(
    (error: unknown, context?: SearchErrorContext) => {
      const { supportInfo } = formatPersistentSnackbarError(error);
      const enrichedSupportInfo: SupportInfo = { ...(supportInfo ?? {}) };
      if (currentUser?.roles?.length) {
        const selected = currentUser.roles.find((r) => r.id === currentUser.selectedRole);
        if (selected?.name) {
          enrichedSupportInfo.role = selected.name;
        }
      }

      const showSearchSnackbar = (
        message: string,
        severity?: SnackbarSeverity,
        autoHideDuration?: number | null,
        title?: string
      ) => {
        showSnackbar(message, severity, autoHideDuration, title, enrichedSupportInfo);
      };

      handleSearchError(error, showSearchSnackbar, contextLabel, { ...context, bugReportUrl, dataManagerEmail });
      reportSearchFailure();
    },
    [showSnackbar, currentUser, contextLabel, reportSearchFailure, bugReportUrl, dataManagerEmail]
  );

  return { handleGridSearchError };
}
